/**
 * WebSocket service for real-time notifications
 */

import { WS_URL } from '../../config.js';

/**
 * Pure helper: build the full WebSocket connection URL.
 * Handles base URLs with or without the /notifications path segment,
 * ensuring the path appears exactly once before appending ?token=.
 *
 * Exported for testability.
 *
 * @param {string} baseUrl - WS_URL from config (may or may not include /notifications)
 * @param {string} token   - JWT token for the $connect authorizer
 * @returns {string} Full WebSocket URL: wss://host/notifications?token=<encoded>
 */
export function buildWebSocketUrl(baseUrl, token) {
  let url = baseUrl.replace(/\/+$/, ''); // trim trailing slashes

  // Append /notifications only if not already present
  if (!url.endsWith('/notifications')) {
    url += '/notifications';
  }

  return `${url}?token=${encodeURIComponent(token)}`;
}

/**
 * Redact query parameters from a URL for safe logging.
 * @param {string} url
 * @returns {string} URL with query values replaced by ***
 */
export function redactUrl(url) {
  return url.replace(/\?.*$/, '?***');
}

class NotificationWebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.listeners = new Set();
    this.isConnecting = false;
    this.shouldReconnect = true;
    this.currentToken = null; // Store current token for reconnection
  }

  /**
   * Connect to WebSocket server
   * @param {string} token - JWT token for authentication
   */
  connect(token) {
    // Check if WebSocket URL is available
    if (!WS_URL) {
      console.log('WebSocket URL not configured, skipping WebSocket connection');
      this.notifyListeners({
        type: 'connection',
        status: 'unavailable',
        reason: 'WebSocket URL not configured'
      });
      return;
    }

    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket connection already in progress');
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    if (!token || typeof token !== 'string' || token === 'undefined') {
      console.warn('Invalid token provided to WebSocket service');
      return;
    }

    // Store token for reconnection attempts
    this.currentToken = token;
    this.isConnecting = true;
    this.shouldReconnect = true;

    try {
      const wsUrl = buildWebSocketUrl(WS_URL, token);
      
      console.log('Connecting to WebSocket:', redactUrl(wsUrl));
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    this.shouldReconnect = false;
    this.reconnectAttempts = 0;
    this.currentToken = null; // Clear stored token
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  /**
   * Add a listener for notifications
   * @param {Function} callback - Function to call when notification received
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Remove a listener
   * @param {Function} callback - Function to remove
   */
  removeListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * Handle WebSocket open event
   */
  handleOpen() {
    console.log('WebSocket connected');
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000; // Reset delay
    
    // Notify listeners of connection
    this.notifyListeners({
      type: 'connection',
      status: 'connected'
    });
  }

  /**
   * Handle WebSocket message
   * @param {MessageEvent} event 
   */
  handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      console.log('WebSocket message received:', message);
      
      if (message.type === 'notification') {
        this.notifyListeners(message);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  /**
   * Handle WebSocket close event
   * @param {CloseEvent} event 
   */
  handleClose(event) {
    console.log('WebSocket closed:', event.code, event.reason);
    this.isConnecting = false;
    
    // Notify listeners of disconnection
    this.notifyListeners({
      type: 'connection',
      status: 'disconnected',
      code: event.code,
      reason: event.reason
    });

    // Attempt to reconnect if it wasn't a clean close and we should reconnect
    if (this.shouldReconnect && event.code !== 1000) {
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket error event
   * @param {Event} event 
   */
  handleError(event) {
    const safeMeta = {
      type: event?.type,
      url: this.ws?.url ? redactUrl(this.ws.url) : undefined,
    };
    console.error('WebSocket error occurred:', safeMeta);
    this.isConnecting = false;
    
    // Notify listeners of error (do not forward raw event — may contain token-bearing URL)
    this.notifyListeners({
      type: 'connection',
      status: 'error',
      message: 'WebSocket connection failed',
      url: safeMeta.url,
    });
  }

  /**
   * Schedule a reconnection attempt
   */
  scheduleReconnect() {
    if (!this.shouldReconnect || this.reconnectAttempts >= this.maxReconnectAttempts || !this.currentToken) {
      console.log('Max reconnection attempts reached, reconnection disabled, or no token available');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);
    
    console.log(`Scheduling WebSocket reconnection attempt ${this.reconnectAttempts} in ${delay}ms`);
    
    setTimeout(() => {
      if (this.shouldReconnect && this.currentToken) {
        this.connect(this.currentToken);
      }
    }, delay);
  }

  /**
   * Notify all listeners of an event
   * @param {Object} data - Event data to send to listeners
   */
  notifyListeners(data) {
    this.listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in WebSocket listener:', error);
      }
    });
  }

  /**
   * Get current connection status
   */
  getStatus() {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN: return 'connected';
      case WebSocket.CLOSING: return 'closing';
      case WebSocket.CLOSED: return 'disconnected';
      default: return 'unknown';
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Create a singleton instance
const notificationWebSocketService = new NotificationWebSocketService();

export default notificationWebSocketService;
